import type { ConnectionSummary, DatasetFacts } from '@/shared/api/generated/catalog';

/* The catalogue drill-down's presentation decisions, as functions rather than JSX.
 *
 * Everything here is a judgement about what a figure *means*, which is why it is testable without
 * rendering anything. The recurring one, at every level: a number this platform did not measure must
 * not be shown as a number it did.
 */

/** Whether a source's catalogue is currently being rebuilt, and how far along.
 *
 * Its own type rather than a pair of numbers because the *reason* to show progress is that a harvest
 * is running — `discovered` and `observed` are stale figures from the last finished run otherwise,
 * and rendering `8 of 8` beside a source nobody is harvesting reads as a live measurement.
 */
export type HarvestActivity =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running'; readonly observed: number; readonly discovered: number }
  /* Running with no `discovered` yet. A harvest reports its total once it has listed the source, so
   * the first seconds of a real run land here — and `0 of 0` would read as an empty database. */
  | { readonly kind: 'starting' };

const RUNNING_STATES: ReadonlySet<string> = new Set(['pending', 'running']);

export function harvestActivity(summary: ConnectionSummary): HarvestActivity {
  if (summary.harvest_state === null || !RUNNING_STATES.has(summary.harvest_state)) {
    return { kind: 'idle' };
  }
  if (summary.discovered <= 0) return { kind: 'starting' };
  return { kind: 'running', observed: summary.observed, discovered: summary.discovered };
}

/** How much of a source has been scored, as a fraction to render — or why there is no fraction.
 *
 * Three outcomes, and collapsing any two of them puts a false statement on a card:
 *
 * * `notConsulted` — `scored` is null, so scoring was not asked. **Never rendered as 0**, which
 *   would claim an estate nobody has ever scored.
 * * `nothingToScore` — the source has no datasets, so "0 of 0" is arithmetically true and
 *   editorially useless: there is nothing here yet, which the catalogue state already says.
 * * `measured` — a real pair. `covered` can legitimately be 0, and that *is* a finding.
 */
export type ScoredCoverage =
  | { readonly kind: 'notConsulted' }
  | { readonly kind: 'nothingToScore' }
  | { readonly kind: 'measured'; readonly covered: number; readonly total: number };

export function scoredCoverage(summary: ConnectionSummary): ScoredCoverage {
  if (summary.scored === null) return { kind: 'notConsulted' };
  if (summary.datasets === 0) return { kind: 'nothingToScore' };
  return { kind: 'measured', covered: summary.scored, total: summary.datasets };
}

/** A dataset's profile figures, or the reason there are none.
 *
 * `facts` is `undefined` for a dataset the facts read never returned — which the API defines as
 * never profiled — and that is a different sentence from a profile that ran and measured an empty
 * table. Both are here because the screen has to say different things.
 *
 * `unread` covers the request itself failing. Without it a failed facts read would render as "never
 * profiled" on every row: a claim about the customer's data used to report a failure of ours, which
 * is the class this codebase records at four AI screens.
 */
export type ProfileReading =
  | { readonly kind: 'unread' }
  | { readonly kind: 'neverProfiled' }
  | { readonly kind: 'profiledEmpty'; readonly at: string }
  | { readonly kind: 'measured'; readonly rows: number; readonly at: string };

export function profileReading(
  facts: DatasetFacts | undefined,
  { unread }: { readonly unread: boolean },
): ProfileReading {
  if (unread) return { kind: 'unread' };
  if (facts === undefined) return { kind: 'neverProfiled' };
  if (facts.last_profiled_at === null) return { kind: 'neverProfiled' };
  if (facts.rows_at_last_profile === null) {
    return { kind: 'profiledEmpty', at: facts.last_profiled_at };
  }
  return {
    kind: 'measured',
    rows: facts.rows_at_last_profile,
    at: facts.last_profiled_at,
  };
}
